import { Component, Input, ViewChild, OnInit } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { Title } from '@angular/platform-browser'
import { NuclearObservable } from '../nuclear-observable'
import { BaseComponent } from './base.component'
import { Writer, Customer, User, CustomerWriterAccess, Account } from '../types'
import sortBy from 'lodash-es/sortBy'
import take from 'lodash-es/take'
import { WriterActions } from '../actions/writer-actions'
import { CustomerWriterAccessActions } from '../actions/customer-writer-access-actions'
import { UiActions } from '../actions/ui-actions'
import { customerGetters } from '../stores/customer-store'
import { userGetters } from '../stores/user-store'
import { WriterCustomerPostsList } from './writer-customer-posts-list/writer-customer-posts-list.component'
import { WriterAccountPostsList } from './writer-account-posts-list/writer-account-posts-list.component'
import { ConfirmBlockModal } from './modals/confirm-block-modal/confirm-block-modal.component'
import { Apollo } from 'apollo-angular'
import gql from 'graphql-tag'

interface Data {
  writer: {
    id: number;
    posts: {
      entries: Post[]
    }
  }
};

interface Post {
  id: number;
  title: string;
  content: string;
}

const writerProfileQuery = gql`
  query WriterProfileWriterQuery($slug: String) {
    writer(slug: $slug) {
      posts(state: ["sampled"], order: "updatedAt DESC") {
        entries {
          id
          title
          content
        }
      }
    }
  }
`

@Component({
  selector: 'writer-profile',
  styles: [`
    th, td {
      white-space: nowrap;
    }
    .writer-img {
      padding-top: 9px;
      width: 100%;
      height: auto;
      max-width: 200px;
    }
  `],
  template: `
    <confirm-block-modal *ngIf="showConfirmation" (onClose)="finishedConfirmation($event)" [account]="account" [writer]="writer" [customer]="customer" [context]="preferenceLevel"></confirm-block-modal>
    <div class="row" id="writer-profile">
      <div class="col-sm-9">
        <div class="row mt-3">
          <div class="col-12">
            <h3 class="mt-3" *ngIf="writerViewingOwnProfile">Configure your image using <a href="https://en.gravatar.com/emails" target="_blank">Gravatar</a></h3>
            <div class="row">
              <div class="col-md-3 col-sm-12 pr-0">
                <div class="gravatar-image">
                <img *ngIf="showAsAnonymous" src="/assets/user-light-bg.png" width="150">
                <img *ngIf="!showAsAnonymous" [attr.alt]="writer?.shortDisplayName" class="gravatar writer-img" [attr.src]="writer.avatar">
                <div *ngIf="customer && writer && !writer.suspended" class="small-top-buffer small-bottom-buffer"> <!-- Have to retrieve customerWriterAccess for preferred, blocked, etc...-->
                  <div class="btn-group btn-block" dropdown autoClose="always">
                    <button class="btn dropdown-toggle btn-block mr-2" [ngClass]="buttonClass()" dropdownToggle>
                      {{ accessLabel() }}&nbsp;
                      <span class="caret"></span>
                    </button>
                    <ul class="dropdown-menu" *dropdownMenu>
                      <li>
                        <table class="table borderless">
                          <tr>
                            <th *ngIf="showBothLevels()"></th>
                            <th>Preferred</th>
                            <th>Blocked</th>
                            <th>No Preference</th>
                          </tr>

                          <tr *ngIf="showBothLevels()">
                            <th>This client</th>
                            <td class="text-center"><input type="radio" name="customerAccess" value="preferred" [ngModel]="customerAccess" (ngModelChange)="changeCustomerPreference('preferred')"></td>
                            <td class="text-center"><input type="radio" name="customerAccess" value="blocked" [ngModel]="customerAccess" (ngModelChange)="changeCustomerPreference('blocked')"></td>
                            <td class="text-center"><input type="radio" name="customerAccess" value="none" [ngModel]="customerAccess" (ngModelChange)="changeCustomerPreference('none')"></td>
                          </tr>
                          <tr>
                            <th *ngIf="showBothLevels()">All clients</th>
                            <td class="text-center"><input type="radio" name="accountAccess" value="preferred" [ngModel]="accountAccess" (ngModelChange)="changeAccountPreference('preferred')"></td>
                            <td class="text-center"><input type="radio" name="accountAccess" value="blocked" [ngModel]="accountAccess" (ngModelChange)="changeAccountPreference('blocked')"></td>
                            <td class="text-center"><input type="radio" name="accountAccess" value="none"  [ngModel]="accountAccess" (ngModelChange)="changeAccountPreference('none')"></td>
                          </tr>
                        </table>
                      </li>
                      <li class="divider"></li>
                      <li><a href="https://help.verblio.com/my-account/managing-content/how-to-prefer-and-block-writers" target="_blank">Help</a></li>
                    </ul>
                  </div>
                  </div>
                </div>
              </div>
              <div class="col-md-9 col-sm-12">
                <h1 *ngIf="showAsAnonymous; else displayName" class="mt-3" >
                  <span >Anonymous</span>
                </h1>
                <ng-template #displayName>
                  <div class="mt-3 d-flex align-items-center">
                    <h1>{{writer?.displayName}}</h1>
                    <i *ngIf="writer?.state == 'unavailable'"
                      class="fas fa-circle fa-xs text-red ml-2"
                      tooltip="Writer is unavailable"
                      placement="top"
                      containerClass="white-tooltip"
                      >
                    </i>
                  </div>
                </ng-template>
                <h3 *ngIf="writer?.suspended" class="text-danger">This writer is no longer active with Verblio.</h3>
                <p *ngIf="!showAsAnonymous && !writer.suspended" [class.profile-published-comment]="writer && !writer.bio">
                  <span *ngIf="writer?.bio" [innerHTML]="writer.bio"></span>
                  <span *ngIf="!writer?.bio">This writer has chosen not to share a bio.</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        <h6>&nbsp;</h6>

        <div class="row">
          <div class="col">
            <!--<base href="/profiles/:slug">-->
            <tabset>

              <!-- CUSTOMER REVIEWS -->
              <tab heading="Customer reviews" (selectTab)="setTabState('reviews')" [active]="tab === 'reviews'">
                <ul *ngIf="writer?.publishedReviews?.length > 0" class="list-unstyled">
                  <li *ngFor="let comment of writer.publishedReviews" class="profile-published-comment">"{{comment}}"</li>
                </ul>
                <div class="profile-published-comment" *ngIf="writer?.publishedReviews?.length === 0">
                  This writer has chosen not to publish any comments.
                </div>
              </tab>

              <tab *ngIf="writer && showSamplesTab" heading="Samples" (selectTab)="setTabState('sampledPosts')" [active]="tab === 'sampledPosts'">
                <div *ngIf="sampledPosts.length > 0; else noSampledPosts;" class="top-buffer">
                  <div>
                    {{sampledPosts.length}}
                    <span *ngIf="sampledPosts.length === 1">sample</span>
                    <span *ngIf="sampledPosts.length !== 1">samples</span>
                  </div>
                  <div class="divider bottom-buffer"></div>
                  <div *ngFor="let post of sampledPosts" class="sample-post">
                    <div class="d-flex flex-row justify-content-between align-items-center cursor-pointer" (click)="expanded[post.id] = !expanded[post.id]">
                      <div class="bold">
                        {{post.title ? post.title : 'No Title'}}
                      </div>
                      <div class="fa" [class.fa-caret-down]="expanded[post.id]" [class.fa-caret-right]="!expanded[post.id]"></div>
                    </div>
                    <div *ngIf="expanded[post.id]">
                      <hr>
                      <div class="small-top-buffer" [innerHTML]="post.content"></div>
                    </div>
                  </div>
                </div>
                <ng-template #noSampledPosts>
                  <p class="top-buffer">{{writer?.shortDisplayName}} hasn't sampled any pieces of content yet.</p>
                </ng-template>
              </tab>

              <!-- YOUR POSTS (1 CUSTOMER) -->
              <tab *ngIf="writer && !hidePostsTab" heading="{{customer?.businessName}}'s posts" (selectTab)="setTabState('posts')" [active]="tab === 'posts'"> <!-- Template heading not working in earlier version of ngx-bootstrap - for small and large screens "Your posts by this writer"-->
                <writer-customer-posts-list *ngIf="tab === 'posts' && writer && customer" [tab]="tab" [writer]="writer" [customer]="customer" (postsLoaded)="updateTabState()"></writer-customer-posts-list>
              </tab>

              <!-- ACCOUNT POSTS (ALL CUSTOMERS) -->
              <tab *ngIf="writer && account?.customersCount > 1" heading="All Posts Across Account" (selectTab)="setTabState('accountPosts')" [active]="tab === 'accountPosts'">
                <writer-account-posts-list *ngIf="tab === 'accountPosts' && writer && customer && account" [tab]="tab" [writer]="writer" [customer]="customer" [account]="account"></writer-account-posts-list>
              </tab>
            </tabset>
          </div>
        </div>
      </div>
      <div class="col-sm-3">
        <div class="text-center top-buffer">
          <a *ngIf="writerViewingOwnProfile" class="btn btn-primary bottom-buffer" href="{{'/writer_profiles/' + writer?.id + '/edit'}}">Edit my profile</a>
          <img *ngIf="writer?.level > 0" src="{{'/assets/badges/level-' + writer?.level + '.png'}}" alt="{{'level ' + writer?.level + ' badge'}}" width="150">
          <p *ngIf="writer?.level === 0" class="wrap">Getting your first badge is as easy as writing your first post!</p>
          <p>Posts published: {{writer?.postsPublished}}</p>
          <table *ngIf="topIndustries().length > 0" class="table">
            <tr class="text-left">
              <th>Category</th>
              <th><div class="float-right">Posts</div></th>
            </tr>
            <tr *ngFor="let industry of topIndustries()" class="text-left">
              <td>{{industry.name}}</td>
              <td><div class="float-right">{{industry.count}}</div></td>
            </tr>
          </table>
        </div>
      </div>
    </div>
  `
})
export class WriterProfile extends BaseComponent implements OnInit {
  @Input() writerSlug: string;
  @Input() customerId: number;
  @ViewChild(WriterCustomerPostsList) writerCustomerPostsList: WriterCustomerPostsList;
  @ViewChild(WriterAccountPostsList) writerAccountPostsList: WriterAccountPostsList;
  @ViewChild(ConfirmBlockModal) confirmBlockModal: ConfirmBlockModal;

  expanded: object = {};
  data: Data;
  writer: Writer;
  customer: Customer;
  account: Account;
  private currentUser: User;

  showConfirmation: boolean = false;
  preferenceLevel: 'account' | 'customer' | 'none' = 'none';

  hidePostsTab: boolean = false; // Triggered when posts are loaded and there are none
  sampledPosts: Post[] = []

  private DEFAULT_ACCESS: 'preferred' | 'blocked' | 'none' = 'none';
  customerAccess: string = this.DEFAULT_ACCESS; // Set selected access when it is retrieved using the writer and customer
  accountAccess: string = this.DEFAULT_ACCESS; // Set selected access when it is retrieved using the writer and customer

  tab: string;
  private labels: Object = {
    preferred: 'Preferred',
    blocked: 'Blocked',
    none: 'Manage Writer',
  };

  constructor(
    private route: ActivatedRoute,
    private writerActions: WriterActions,
    private customerWriterAccessActions: CustomerWriterAccessActions,
    private uiActions: UiActions,
    private apollo: Apollo,
    private titleService: Title) {
    super()
  }

  ngOnInit(): void {
    const routeObs = this.route.parent.params.subscribe(params => {
      this.clearRouteObservers()
      const uiState = NuclearObservable.currentValue<Object>(userGetters.uiState, { toJS: true })
      const [theTab, theCustomerId] = [uiState['writerProfileTab'], uiState['writerProfileCustomerId']]
      this.tab = (theCustomerId === this.customerId ? theTab : 'posts') || 'posts'

      if (this.writerSlug) {
        this.writerActions.loadWriterBySlug(this.writerSlug, { aggregateData: true }).then(() => {
          const writerId = NuclearObservable.currentValue<number>(customerGetters.writerIdFromSlug(this.writerSlug))
          this.afterWriterIdLoad(writerId)

          const userObs = NuclearObservable.from<User>(userGetters.user, { toJS: true }).subscribe(u => {
            this.currentUser = u
            if (this.showSamplesTab) {
              this.apollo.watchQuery<Data>({
                query: writerProfileQuery,
                variables: {slug: this.writerSlug},
                notifyOnNetworkStatusChange: true,
                fetchPolicy: 'cache-and-network'
              }).valueChanges.subscribe(({ data }) => {
                if (data) {
                  this.sampledPosts = data.writer.posts.entries
                }
              })
            }
          })

          this.observers.push(userObs)
        })
      }
    })
    const accountObs = NuclearObservable.from<Account>(customerGetters.aggregateAccount, { toJS: true }).subscribe(a => this.account = a)
    this.observers.push(routeObs, accountObs)
  }

  get showAsAnonymous(): boolean {
    return !this.writer || (this.writer.anonymous && this.viewingPublicProfile)
  }

  get viewingPublicProfile(): boolean {
    return !this.customerViewing && !this.writerViewingOwnProfile
  }

  get customerViewing(): boolean {
    return this.currentUser && this.currentUser.role === 'customer'
  }

  get writerViewingOwnProfile(): boolean {
    return this.currentUser && this.currentUser.role === 'writer' && this.currentUser.userableId === this.writer.id
  }

  get showSamplesTab(): boolean {
    return !this.showAsAnonymous
  }

  showBothLevels(): boolean {
    return this.preferenceLevel === 'customer' || (this.account && this.account.customersCount > 1 || this.account.agency)
  }

  // Fetch all the data that depends on having a writerId
  afterWriterIdLoad(writerId: number): void {
    this.writer = NuclearObservable.currentValue<Writer>(customerGetters.writer(writerId), { toJS: true })

    // SET THE TITLE
    let title = this.showAsAnonymous ? 'Anonymous profile' : `${this.writer && this.writer.shortDisplayName}'s profile`
    this.titleService.setTitle(title)


    // LOAD THE ACCESS
    let customerWriterAccessForCustomer = NuclearObservable.currentValue<CustomerWriterAccess>(customerGetters.customerWriterAccess(this.writer.id, this.customerId), { toJS: true })
    let customerWriterAccessForAccount = NuclearObservable.currentValue<CustomerWriterAccess>(customerGetters.customerWriterAccessForAccount(this.writer.id), { toJS: true })
    if (customerWriterAccessForCustomer) this.customerAccess = this.readAccess(customerWriterAccessForCustomer)
    if (customerWriterAccessForAccount) this.accountAccess = this.readAccess(customerWriterAccessForAccount)
    if (this.writer && this.customerId) {
      this.preferenceLevel = 'account'
      this.customerWriterAccessActions.loadCustomerWriterAccess(this.writer.id, this.customerId).then(() => {
        customerWriterAccessForCustomer = NuclearObservable.currentValue<CustomerWriterAccess>(customerGetters.customerWriterAccess(this.writer.id, this.customerId), { toJS: true })
        this.customerAccess = this.readAccess(customerWriterAccessForCustomer)
        if (customerWriterAccessForCustomer) this.preferenceLevel = 'customer'
      })
      this.customerWriterAccessActions.loadCustomerWriterAccessForAccount(this.writer.id).then(() => {
        customerWriterAccessForAccount = NuclearObservable.currentValue<CustomerWriterAccess>(customerGetters.customerWriterAccessForAccount(this.writer.id), { toJS: true })
        this.accountAccess = this.readAccess(customerWriterAccessForAccount)
      })
    }

    if (this.customerId) {
      const custObs = NuclearObservable.from<Customer>(customerGetters.customer(this.customerId), { toJS: true }).subscribe(c => this.customer = c)
      this.routeObservers.push(custObs)
    } else {
      // Writer is viewing their own profile or someone is viewing public profile
      this.tab = 'reviews'
      this.hidePostsTab = true
    }
  }

  //Since default tab state is posts, if there are no posts, when loaded, switch to reviews tab
  updateTabState(): void {
    const customer = NuclearObservable.currentValue<Customer>(customerGetters.customer(this.customerId), { toJS: true })
    const postCount = !!customer && !!customer.postCounts && customer.postCounts[this.writer.id]
    if (!postCount && this.tab === 'posts') {
      this.hidePostsTab = true
      this.setTabState('reviews')
    }
  }

  setTabState(tab: string): void {
    this.tab = tab
    this.uiActions.setUiState('writerProfileTab', tab)
    this.uiActions.setUiState('writerProfileCustomerId', this.customerId)
  }

  readAccess(customerWriterAccess: CustomerWriterAccess): string {
    return (customerWriterAccess && customerWriterAccess.access) ? customerWriterAccess.access : this.DEFAULT_ACCESS
  }

  relevantAccess(): string {
    return this.customerAccess === 'none' ? this.accountAccess : this.customerAccess
  }

  accessLabel(): string {
    return this.labels[this.relevantAccess()]
  }

  buttonClass(): string {
    const relevantAccess = this.relevantAccess()
    if (relevantAccess === 'preferred') {
      return 'btn-success'
    } else if (relevantAccess === 'blocked') {
      return 'btn-danger'
    } else {
      return 'btn-primary'
    }
  }

  changeCustomerPreference(access: 'preferred' | 'blocked'): void {
    if (access === 'blocked') {
      this.preferenceLevel = 'customer'
      this.showConfirmation = true
    } else {
      this.customerAccess = access
      this.customerWriterAccessActions.updateCustomerWriterAccess(this.writer.id, this.customerId, {access: access})
    }
  }

  changeAccountPreference(access: 'preferred' | 'blocked'): void {
    if (access === 'blocked') {
      this.preferenceLevel = 'account'
      this.showConfirmation = true
    } else {
      this.accountAccess = access
      this.customerWriterAccessActions.updateCustomerWriterAccessForAccount(this.writer.id, {access: access})
    }
  }

  finishedConfirmation(confirmed: boolean): void {
    this.showConfirmation = false
    const accessBefore = this.preferenceLevel === 'account' ? this.accountAccess : this.customerAccess
    if (confirmed) {
      if (this.preferenceLevel === 'account') {
        this.accountAccess = 'blocked'
        this.customerWriterAccessActions.updateCustomerWriterAccessForAccount(this.writer.id, {access: 'blocked'}).then(() => {
          this.reloadPosts() // Queued posts become declined
        })
      } else if (this.preferenceLevel === 'customer') {
        this.customerAccess = 'blocked'
        this.customerWriterAccessActions.updateCustomerWriterAccess(this.writer.id, this.customerId, {access: 'blocked'}).then(() => {
          this.reloadPosts() // Queued posts become declined
        })
      }
    } else {
      if (this.preferenceLevel === 'account') {
        this.accountAccess = accessBefore
      } else {
        this.customerAccess = accessBefore
      }
    }

  }

  reloadPosts(): void {
    if (this.tab === 'posts' && this.writerCustomerPostsList) {
      this.writerCustomerPostsList.queryRef.refetch()
    } else if (this.tab === 'accountPosts') {
      this.writerAccountPostsList.queryRef.refetch()
    }
  }

  topIndustries(): { name: string, count: number }[] {
    if (this.writer) {
      return take(sortBy(this.writer.postsByIndustry, pbi => pbi.count).reverse(), 10)
    } else {
      return []
    }
  }
}
